import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import { DatePicker, DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { useSaveDealAndNavigate } from 'api/aws/deals/useSaveDealAndNavigate'
import { useEntitiesFromSearch } from 'api/search'
import ExistingDeal from 'components/CreateDeal/ExistingDealSelect'
import SelectAgreementOption from 'components/CreateDeal/SelectAgreementOption'
import SelectInvoiceOption from 'components/CreateDeal/SelectInvoiceOption'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { initialDealState } from 'components/Deals/grid/initialDealState'
import { ROLES } from 'config/roles.config'
import FormLoading from 'components/Loading/FormLoading'
import FloatingHelpButton from 'context/HelpProvider/FloatingHelpButton'
import FormHelpPopover from 'context/HelpProvider/FormHelpPopover'
import HelpNavigationBox from 'context/HelpProvider/HelpNavigation'
import HelpOverlay from 'context/HelpProvider/HelpOverlay'
import { useHelp } from 'context/HelpProvider/HelpProvider'
import { useCreateNewDealRefs } from 'context/HelpProvider/useCreateNewDealRefs'
import { useHighlightStep } from 'context/HelpProvider/useHighlightStep'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { dialogStyles as formDialogStyles } from 'styles/utilities'
import { Contact, Deal, NewDeal, Organization, OrganizationTableItem } from 'types'
import {
  calculateNextVersion,
  getActiveVersions,
  getHighestVersion,
  normalizeTypeForVersioning,
  VersionChangeType,
  VersionedDeal,
} from 'utils/agreementVersioning'
import { useUpdateItems } from '../../api/aws/useUpdateItems'
import { useContactsById } from '../../api/customHooks/useContactsById'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { BoxScroller, CreateProjectTextField } from '../../styles/elements/styleElements'
import { normalizeDate } from '../../utils/dateUtils'
import ContactsAutocomplete from '../Contacts/ContactsAutocomplete'
import DealTypeSelect from '../Deals/DealTypeSelect'
import EditVerticalAutocomplete from '../EditModeComponents/EditVerticalAutocomplete'
import DealsAutocomplete from '../TeamTasks/DealsAutocomplete'
import AgreementComparisonDialog from './AgreementComparisonDialog'
import CreateDealDisclaimer from './CreateDealDisclaimer'
import CreateDealHeader from './CreateDealHeader'
import CreateDealOrgAutocomplete from './CreateDealOrgAutocomplete'
import { getUniqueIdentifier } from './getUniqueIdentifier'
import SelectChangeType, { ChangeTypeValue } from './SelectChangeType'
import StageDefaultTextInput from './StageDefaultTextInput'
import { dialogContentStyles } from './styles'
import { useSyncSelectedContacts } from './useSyncSelectedContacts'
import { useVersionDetection } from './useVersionDetection'
import ValidateDiscovery from './ValidateDiscovery'
import ValidateDiscoveryIcons from './ValidateDiscoveryIcons'
import VersionAmendmentDialog from './VersionAmendmentDialog'
import VersionInfoBanner from './VersionInfoBanner'

const verticalsRequiringApproval = [
  'Construction',
  'Tourism',
  'Marketing Agency',
  'Manufacturing',
  'Entertainment',
  'Restaurant (Food & Beverage)',
  'City/Government',
  'Financial Institution',
  'Technology',
  'Medical',
  'Lawyer',
  'Education',
  'Senior',
  'Homebuilding',
  'Build to rent',
]
const CreateVersionContract = ({ open, onClose }: { open: any; onClose: any }) => {
  const { currentStep, startHelp } = useHelp()
  const { activeUser, isActiveUserLoading, isActiveUserError } = useActiveUser()
  const [newDeal, setNewDeal] = useState<NewDeal>(initialDealState)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization>(null)
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])
  const [selectedDeal, setSelectedDeal] = useState<Deal>(null)
  const { organizations, products: allProducts, deals } = useEntitiesFromSearch()
  const products = allProducts?.filter(p => p?.PK === selectedOrganization?.PK)
  const organizationDeals = deals?.filter(d => d?.PK === selectedOrganization?.PK)

  // Count active MSA/MSA-Housing deals per org for autocomplete display
  const activeContractCountByOrg = useMemo(() => {
    const map = new Map<string, number>()
    if (!deals) return map
    const msaTypes = new Set(['MSA', 'RMSA', 'MSA - Housing', 'RMSA - Housing'])
    const terminalStatuses = new Set(['Ended', 'Ended (system)', 'Cancelled', 'Replaced'])
    for (const deal of deals) {
      if (
        msaTypes.has(deal?.type ?? '') &&
        deal?.stage === 'Deal Won' &&
        !terminalStatuses.has(deal?.dealStatus ?? '')
      ) {
        const pk = deal.PK
        map.set(pk, (map.get(pk) || 0) + 1)
      }
    }
    return map
  }, [deals])

  const { data: contacts, isLoading: isContactsLoading, isError: isContactsError } = useContactsItems()
  const { mutate: updateItems } = useUpdateItems()
  const [contactIds, setContactIds] = useState([])
  const {
    newContacts: fetchedContacts,
    isContactByIdLoading: fetchedContactsLoading,
    isContactsByIdError: fetchedContactsError,
  } = useContactsById(contactIds || [], 'createNewDeal')
  const [localStage, setLocalStage] = useState<string>(newDeal?.stage)
  const [openTooltip, setOpenTooltip] = useState(false) // To control tooltip visibility
  const [isCreating, setIsCreating] = useState(false)
  const [isError, setIsError] = useState(false)
  const theme = useTheme()
  const { showSnackbar } = useSnackbarContext()
  const [validationErrors, setValidationErrors] = useState({
    organization: false,
    type: false,
    startDate: false,
    endDate: false,
    selectedDeal: false,
    agreementOption: false,
    invoiceOption: false,
    changeType: false,
  })
  // TODO: Refine this and use instead of all deals and all products
  const [isValid, setIsValid] = useState(false)
  const [missingFields, setMissingFields] = useState([])
  const [iconColor, setIconColor] = useState('gray')
  const [noSignature, setNoSignature] = useState(false)
  const [dealTypeName, setDealTypeName] = useState('')
  const testUsers = [ROLES.SYSTEM_ADMIN]
  const [agreementOption, setAgreementOption] = useState('')
  const [invoiceOption, setInvoiceOption] = useState('')
  const [changeType, setChangeType] = useState<ChangeTypeValue>('')
  const [selectedAgreementIndex, setSelectedAgreementIndex] = useState(0)
  const [useVersioning, setUseVersioning] = useState(true)
  const [showComparisonDialog, setShowComparisonDialog] = useState(false)
  const [selectedVersionOverride, setSelectedVersionOverride] = useState<VersionedDeal | null>(null)
  const [showAmendmentDialog, setShowAmendmentDialog] = useState(false)

  // Version detection
  const {
    shouldShowVersionFlow: hasVersionableAgreements,
    versionableAgreements,
    currentVersion,
    activeMasterAgreement,
  } = useVersionDetection({
    selectedOrganization,
    deals,
    products: allProducts,
  })

  // Show versioning flow only when detected AND user hasn't opted out
  const shouldShowVersionFlow = hasVersionableAgreements && useVersioning

  // When multiple agreements exist, use selected index
  const activeAgreement = versionableAgreements[selectedAgreementIndex] ?? activeMasterAgreement

  // Detect multiple active versions within the selected agreement
  const activeVersionsForAgreement = useMemo(() => (activeAgreement ? getActiveVersions(activeAgreement) : []), [
    activeAgreement,
  ])
  const hasMultipleActiveVersions = activeVersionsForAgreement.length > 1

  // Use override if set, otherwise fall back to agreement's current version
  const activeCurrentVersion = selectedVersionOverride ?? activeAgreement?.currentVersion ?? currentVersion

  // Auto-open comparison dialog when multiple active versions are detected
  useEffect(() => {
    if (hasMultipleActiveVersions && shouldShowVersionFlow && !selectedVersionOverride) {
      setShowComparisonDialog(true)
    }
  }, [hasMultipleActiveVersions, shouldShowVersionFlow, selectedVersionOverride])

  const isVersionChangeType = changeType === 'major' || changeType === 'minor' || changeType === 'patch'

  const nextVersion = useMemo(() => {
    if (!activeCurrentVersion || !isVersionChangeType) return ''
    // Use the highest version in the entire chain to avoid collisions
    const baseVersion = activeAgreement ? getHighestVersion(activeAgreement) : activeCurrentVersion.semanticVersion
    return calculateNextVersion(baseVersion, changeType as VersionChangeType)
  }, [activeCurrentVersion, activeAgreement, changeType, isVersionChangeType])

  // Carry products from current version (all change types carry over existing products)
  const carriedProducts = useMemo(() => {
    if (!shouldShowVersionFlow || !activeCurrentVersion) return []
    return activeCurrentVersion.products.map(p => ({
      ...p,
      deal_uuid: undefined,
      PK: undefined,
      SK: undefined,
      instanceId: undefined,
      createdAt: undefined,
    }))
  }, [shouldShowVersionFlow, activeCurrentVersion, changeType])

  const relatedProducts = useMemo(() => {
    if (shouldShowVersionFlow && carriedProducts.length > 0) return carriedProducts
    return selectedDeal ? products?.filter(p => p?.deal_uuid === selectedDeal?.deal_uuid) : []
  }, [shouldShowVersionFlow, carriedProducts, selectedDeal, products])
  const { handleSaveDealAndNavigate } = useSaveDealAndNavigate()
  const filteredDealSelection = useMemo(() => {
    if (!organizationDeals || !organizationDeals?.length) return []
    return organizationDeals?.filter(
      deal => !['Ended', 'Ended (system)', 'Cancelled', 'Replaced']?.includes(deal?.dealStatus) && !deal?.predecessorId
    )
  }, [organizationDeals])

  const fetchContactsForOrganization = organization => {
    // Ensure primaryContactId is an array
    const primaryContactId =
      organization && Array.isArray(organization.primaryContact)
        ? organization.primaryContact
        : organization.primaryContact
        ? [organization.primaryContact]
        : []

    const contactIds = [...primaryContactId, ...(organization.contacts || [])]
    setContactIds(contactIds)
  }
  const handleSelectOrganization = (organization: Organization) => {
    setSelectedOrganization(organization)

    if (organization) {
      fetchContactsForOrganization(organization)
      setNewDeal(prev => ({
        ...prev,
        organizations: [organization.id || organization.org_uuid],
      }))

      // Check if the organization has a valid vertical
      if (!organization.vertical || organization.vertical.includes('No Vertical Assigned')) {
        console.log('No Valid Vertical Assigned')
        setIsValid(false) // Disable buttons
        setValidationErrors(prev => ({
          ...prev,
          organization: true, // Trigger error
          organizationHelperText: 'No valid vertical assigned to the organization', // Set helper text
        }))
      } else {
        setIsValid(true) // Enable buttons
        setValidationErrors(prev => ({
          ...prev,
          organization: false,
          organizationHelperText: '', // Clear the helper text when valid
        }))
      }
    } else {
      setSelectedContacts([])
      setSelectedDeal(null)
      setNewDeal(prev => ({
        ...prev,
        organizations: [],
        contacts: [],
      }))
      setIsValid(false) // Disable buttons if no organization is selected
    }
  }

  useSyncSelectedContacts({
    fetchedContacts,
    setSelectedContacts,
    setNewDeal,
  })
  const allContacts = useMemo(() => {
    const contactMap = new Map()
    contacts?.forEach(contact => contactMap.set(contact.contacts_uuid, contact))
    fetchedContacts?.forEach(contact => contactMap.set(contact.contacts_uuid, contact))
    return Array.from(contactMap.values())
  }, [contacts, fetchedContacts])
  const resetDealState = () => {
    setNewDeal(initialDealState)
    setSelectedOrganization(null)
    setSelectedContacts([])
    setIsError(false)
    setIconColor('gray')
    setNoSignature(false)
    setDealTypeName('')
    setChangeType('')
    setSelectedAgreementIndex(0)
    setUseVersioning(true)
    setSelectedVersionOverride(null)
    setShowComparisonDialog(false)
    setShowAmendmentDialog(false)
  }
  useEffect(() => {
    if (selectedOrganization && newDeal?.type !== 'IO') {
      if (
        selectedOrganization?.vertical &&
        Array.isArray(selectedOrganization?.vertical) &&
        selectedOrganization?.vertical.some(v => verticalsRequiringApproval.includes(v))
      ) {
        setNewDeal(prev => ({
          ...prev,
          isApproved: false,
          needsApproval: true,
          isRequested: false,
        }))
      }
    }
  }, [selectedOrganization, newDeal?.type])

  // useEffect(() => {
  //   if (relatedProducts) {
  //     console.log('relatedProducts', relatedProducts)
  //   }
  // }, [relatedProducts])

  const handleCloseCreateDeal = (event, reason) => {
    if (reason && reason === 'backdropClick') return
    resetDealState()
    setSelectedContacts([])
    setIsError(false)
    onClose()
  }

  const handleChange = event => {
    const { name, value } = event.target
    setNewDeal(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateFields = () => {
    const hasValidVertical =
      selectedOrganization?.vertical && !selectedOrganization.vertical.includes('No Vertical Assigned')

    const endBeforeStart =
      newDeal.startDate && newDeal.endDate && dayjs(newDeal.endDate).isBefore(dayjs(newDeal.startDate), 'day')

    const baseErrors = {
      organization:
        (newDeal && Array.isArray(newDeal.organizations) && newDeal.organizations.length === 0) || !hasValidVertical,
      type: !newDeal.type,
      startDate: !newDeal.startDate,
      endDate: !newDeal.endDate || !!endBeforeStart,
    }

    const errors = hasVersionableAgreements
      ? {
          ...baseErrors,
          changeType: !changeType,
          // Agreement/invoice auto-set for IO/CSA, not needed for versioning
          agreementOption: false,
          invoiceOption: false,
          selectedDeal: false,
        }
      : {
          ...baseErrors,
          changeType: false,
          agreementOption: !agreementOption,
          invoiceOption: !invoiceOption,
          selectedDeal: agreementOption === 'Replace Existing Business' && !selectedDeal,
        }

    setValidationErrors(errors)
    return !Object.values(errors).some(Boolean)
  }
  useEffect(() => {
    setIsValid(validateFields())
  }, [newDeal])

  // Handle vertical change in CreateDeal
  const handleVerticalChange = event => {
    const newVerticals = event.target.value // Get the new vertical values

    // Update the vertical in state without triggering backend update yet
    setSelectedOrganization(prev => ({
      ...prev,
      vertical: newVerticals, // Update the selected organization's vertical
    }))

    setNewDeal(prev => ({
      ...prev,
      organizations: [prev.organizations[0]], // Keep other organization-related fields intact
    }))
  }

  // Handle the blur event to trigger backend update
  const handleVerticalBlur = event => {
    const { name, value } = event.target

    console.log('Vertical Blur Event:', { name, value }) // Debugging the blur event

    // Ensure that a value exists before proceeding
    if (value) {
      // Update the state (trigger the vertical update)
      handleChange({ target: { name, value } })
      handleVerticalUpdate(value) // Call the backend update function
    } else {
      // Handle the case when no value is provided (optional)
      console.warn(`No value provided for ${name}. Setting to default.`)
      handleChange({ target: { name, value: '' } })
      handleVerticalUpdate('') // Send empty value to backend
    }
  }

  // Call this function on blur to trigger the backend update
  const handleVerticalUpdate = updatedVertical => {
    if (!selectedOrganization || !selectedOrganization.org_uuid) {
      console.error('No valid organization selected for vertical update')
      return
    }

    const updates = [
      {
        primaryKey: 'org_uuid',
        tableName: 'organizations',
        itemId: selectedOrganization.org_uuid, // Use the organization's unique ID
        fieldName: 'vertical',
        newValue: updatedVertical, // Send the correct vertical array
      },
    ]

    // Perform the update
    updateItems(
      { updates },
      {
        onSuccess: data => {
          console.log('Vertical updated successfully: ', data)
          showSnackbar('Organization vertical updated successfully', 'success')
          setOpenTooltip(false)
        },
        onError: error => {
          console.error('Error updating vertical: ', error)
          showSnackbar('Error updating organization vertical', 'error')
        },
      }
    )
  }

  // Ensure this function is not called directly in render logic
  const onChangeOrganization = async newValue => {
    if (!newValue) {
      setNewDeal(prev => ({
        ...prev,
        organizations: [],
        name: '',
        dba: '',
        contacts: [],
      }))
      setSelectedContacts([])
      setMissingFields([])
      setIconColor('gray')
      setSelectedOrganization(null)
      setSelectedDeal(null)
      return
    }

    handleSelectOrganization(newValue)
    setSelectedDeal(null)
    const primaryContactIds =
      newValue && newValue.primaryContact && Array.isArray(newValue.primaryContact) ? newValue.primaryContact : []
    const additionalContactIds =
      newValue && newValue.contacts && Array.isArray(newValue.contacts) ? newValue.contacts : []
    const contactIds = [...additionalContactIds, ...primaryContactIds]

    fetchContactsForOrganization(contactIds)

    const newIdentifier = getUniqueIdentifier(deals?.map(deal => deal.name))

    setNewDeal(prev => ({
      ...prev,
      organizations: [newValue.id || newValue.org_uuid],
      name: `${newValue.name} - ${dealTypeName}`, // Combine organization name and deal type
      dba: newValue.dba,
      contacts: contactIds,
    }))

    const newSelectedContacts = contacts.filter(contact => contactIds.includes(contact.contacts_uuid))
    setSelectedContacts(newSelectedContacts)
  }

  const handleAddContact = newValue => {
    setNewDeal(prev => ({
      ...prev,
      contacts: Array.isArray(newValue)
        ? newValue.map(item => item.contacts_uuid)
        : newValue
        ? [newValue.contacts_uuid]
        : [],
    }))
  }

  useEffect(() => {
    if (!open) {
      resetDealState()
      setSelectedContacts([])
      setOpenTooltip(false)
      setIsError(false)
    }
  }, [open])

  // Ref to store the Dialog element
  const {
    dialogRef,
    selectedOrgRef,
    agreementOptionRef,
    stageRef,
    noSignatureRef,
    typeRef,
    nextRef,
    stepRefs,
    createNewDealSteps,
  } = useCreateNewDealRefs()

  const handleDealTypeChange = newValue => {
    const isCSAType = ['CSA', 'RCSA'].includes(newValue)
    const isTypeIO = newValue === 'IO'
    const isTypeMSA = newValue === 'MSA'

    setDealTypeName(newValue)

    setNewDeal(prev => {
      let needsApproval = prev.needsApproval

      // If it's an MSA deal, check if the vertical is "Student" or "Multifamily"
      if (isTypeMSA) {
        const requiresApproval =
          selectedOrganization &&
          selectedOrganization?.vertical &&
          Array.isArray(selectedOrganization?.vertical) &&
          selectedOrganization?.vertical.some(v => verticalsRequiringApproval.includes(v)) &&
          !['Student', 'Multifamily', 'Student Housing'].includes(selectedOrganization?.vertical[0])

        needsApproval = requiresApproval
      }

      return {
        ...prev,
        name: selectedOrganization ? `${selectedOrganization?.name} - ${newValue}` : '',
        type: newValue,
        needsApproval: isCSAType ? true : isTypeMSA ? needsApproval : prev.needsApproval,
      }
    })
  }

  const handleAgreementOption = event => {
    const selectedType = event.target.value
    const isReplacement = selectedType === 'Replace Existing Business'

    if (!isReplacement) {
      setSelectedDeal(null)
      setDealTypeName('')
    }
    setAgreementOption(selectedType)
    setInvoiceOption('')
    setNewDeal(prev => ({
      ...(isReplacement ? prev : initialDealState),
      organizations: prev?.organizations ?? [],
      contacts: prev?.contacts ?? [],
      agreementOption: selectedType,
      isClone: isReplacement,
      invoiceOption: null,
      type: isReplacement ? prev?.type : '',
      startDate: isReplacement ? prev?.startDate : null,
      endDate: isReplacement ? prev?.endDate : null,
    }))
  }
  const handleChangeType = (event: any) => {
    const selected = event.target.value as ChangeTypeValue

    // Handle IO/CSA — stay in dropdown but switch underlying mode
    if (selected === 'io' || selected === 'csa') {
      const dealType = selected === 'io' ? 'IO' : 'CSA'
      setChangeType(selected)
      setUseVersioning(false)
      setAgreementOption('New/Add-on Business')
      setInvoiceOption('New Invoice')
      setDealTypeName(dealType)
      setNewDeal(prev => ({
        ...initialDealState,
        organizations: prev?.organizations ?? [],
        contacts: prev?.contacts ?? [],
        name: selectedOrganization ? `${selectedOrganization.name} - ${dealType}` : '',
        type: dealType,
        agreementOption: 'New/Add-on Business',
        invoiceOption: 'New Invoice',
      }))
      return
    }

    // Handle major/minor/patch — restore versioning mode
    const versionChangeType = selected as VersionChangeType
    setChangeType(versionChangeType)
    setUseVersioning(true)
    setAgreementOption('')
    setInvoiceOption('')

    if (activeCurrentVersion) {
      const predecessor = activeCurrentVersion.deal
      const normalizedType = normalizeTypeForVersioning(predecessor.type)
      setNewDeal(prev => ({
        ...prev,
        type: normalizedType,
        name: `${selectedOrganization?.name} - ${normalizedType}`,
        predecessorId: predecessor.deal_uuid,
        masterAgreementId: activeAgreement?.masterAgreementId,
        contacts: predecessor.contacts ?? prev.contacts,
        organizations: predecessor.organizations ?? prev.organizations,
        owner: predecessor.owner ?? prev.owner,
        primaryContact: predecessor.primaryContact ?? prev.primaryContact,
        recipients: predecessor.recipients ?? prev.recipients,
        versionChangeType,
        versionType: versionChangeType,
        ...(versionChangeType === 'patch' && predecessor.contractId ? { contractId: predecessor.contractId } : {}),
        // Minor (date renewal): keep current dates so user can edit them
        // Major/Patch: start date = predecessor end date + 1 day (no billing gap)
        startDate: versionChangeType === 'minor'
          ? predecessor.startDate ?? prev.startDate
          : predecessor.endDate ? dayjs(predecessor.endDate).add(1, 'day').format('YYYY-MM-DD') : prev.startDate,
        endDate: versionChangeType === 'minor'
          ? predecessor.endDate ?? prev.endDate
          : prev.endDate,
      }))
      setDealTypeName(normalizedType)
    }
  }

  useEffect(() => {
    console.log('newDeal', newDeal)
  }, [newDeal])
  const handleInvoiceOptionChange = event => {
    const selectedInvoiceOption = event.target.value
    setInvoiceOption(selectedInvoiceOption)
    setNewDeal(prev => ({
      ...prev,
      invoiceOption: selectedInvoiceOption, // Add to the deal's properties
    }))
  }
  // const onNoSignatureChange = (name, value, organization) => {
  //   console.log('onNoSignatureChange', { name, value, organization })
  //   // Create a synthetic event object with a target containing the name and value
  //   handleChange({ target: { name, value } })
  // }
  const onNoSignatureChange = (name: string, value: any) => {
    handleChange({ target: { name, value } })

    if (name === 'noSignature') {
      const checked = !!value
      setNoSignature(checked)
      setLocalStage(checked ? 'Deal Won' : newDeal?.stage ?? '')
    }
  }

  useEffect(() => {
    if (selectedDeal) {
      console.log('selectedDeal', selectedDeal)
    }
  }, [selectedDeal])
  const addPredecessorSelection = predecessor => {
    if (!predecessor) {
      // Clear only predecessor-related fields, NOT core deal info
      setNewDeal(prev => ({
        ...prev,
        predecessorId: '',
        isPredecessor: false,
        oldContractId: '',
        startDate: '',
        endDate: '',
      }))
      setSelectedDeal(null)
      return
    }
    setSelectedDeal(predecessor)
    setNewDeal(prev => ({
      ...prev,
      name: `${selectedOrganization?.name} - ${predecessor?.type}`,
      predecessorId: predecessor?.deal_uuid,
      isPredecessor: false,
      isSuccessor: true,
      oldContractId: predecessor?.contractId ?? '',
      recipients: predecessor?.recipients ?? {},
      contacts: predecessor?.contacts ?? prev.contacts, // 👈 don't reset if unnecessary
      organizations: predecessor?.organizations ?? prev.organizations, // 👈 same
      owner: predecessor?.owner ?? prev.owner,
      primaryContact: predecessor?.primaryContact ?? prev.primaryContact,
      startDate: predecessor?.startDate ? predecessor?.startDate : '',
      endDate: predecessor?.endDate ? predecessor?.endDate : '',
      type: predecessor?.type ? predecessor?.type : '',
    }))
  }
  const handleClearDeal = () => {
    setSelectedDeal(null)
  }
  const isLoading = isContactsLoading || isActiveUserLoading
  const handleStartHelp = () => startHelp('createNewDeal')
  useHighlightStep(0, selectedOrgRef)
  useHighlightStep(1, agreementOptionRef)
  useHighlightStep(2, stageRef)
  useHighlightStep(3, noSignatureRef)
  useHighlightStep(4, typeRef)
  useHighlightStep(5, nextRef)

  return (
    <Box>
      <Dialog
        open={open}
        onClose={handleCloseCreateDeal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            ...formDialogStyles({ theme, disabled: !selectedOrganization, value: selectedOrganization }),
            gap: 2,
          },
        }}
        slotProps={{ backdrop: () => {} }}
        disableEscapeKeyDown
      >
        <CreateDealHeader context={shouldShowVersionFlow ? 'Version Contract' : 'Create Deals'} cloneMode="" />
        <BoxScroller
          sx={{
            '&::-webkit-scrollbar-thumb': {
              backgroundColor:
                theme.palette.mode === 'dark' ? theme.palette.background.scrollbar_thumb : alpha(theme.palette.text.primary, 0.5),
              borderRadius: '6px',
            },
          }}
        >
          <DialogContent ref={dialogRef} sx={dialogContentStyles}>
            {isLoading ? (
              <FormLoading />
            ) : (
              <Stack gap={1}>
                <Grid container>
                  <Grid item xs={10}>
                    <Box ref={selectedOrgRef}>
                      <CreateDealOrgAutocomplete
                        organizations={organizations}
                        multiple={false}
                        label={'Select Organization'}
                        value={selectedOrganization}
                        addOrganization={onChangeOrganization}
                        setIsValid={setIsValid}
                        error={validationErrors.organization} // Pass error state
                        openTooltip={openTooltip}
                        setOpenTooltip={setOpenTooltip}
                        isDisabled={agreementOption && agreementOption === 'Replace Existing Business' ? true : false}
                        activeContractCountByOrg={activeContractCountByOrg}
                      />
                    </Box>
                  </Grid>
                  <ValidateDiscoveryIcons
                    organization={selectedOrganization}
                    missingFields={missingFields}
                    isLoading={isLoading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ValidateDiscovery
                    organization={selectedOrganization}
                    missingFields={missingFields}
                    setMissingFields={setMissingFields}
                  />
                </Grid>
                {/* Conditional Fields */}
                <Grid container spacing={0} alignItems="center">
                  {selectedOrganization &&
                    (!Array.isArray(selectedOrganization?.vertical) ||
                      selectedOrganization?.vertical.length === 0 ||
                      selectedOrganization?.vertical.includes('No Vertical Assigned')) && (
                      <Grid item xs={12} sm={6} xl={12}>
                        <EditVerticalAutocomplete
                          name="vertical"
                          value={selectedOrganization.vertical || []}
                          onChange={handleVerticalChange}
                          onBlur={handleVerticalBlur}
                          hasDeals={selectedOrganization?.deals?.length > 0}
                        />
                      </Grid>
                    )}
                  {hasVersionableAgreements ? (
                    <Grid container spacing={1} alignItems="center">
                      {shouldShowVersionFlow && (
                        <Grid item xs={12}>
                          <VersionInfoBanner
                            agreement={activeAgreement}
                            activeVersionCount={activeVersionsForAgreement.length}
                            selectedVersion={selectedVersionOverride}
                            onCompareClick={() => setShowComparisonDialog(true)}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Box ref={agreementOptionRef}>
                          <SelectChangeType
                            changeType={changeType}
                            onChange={handleChangeType}
                            nextVersion={nextVersion}
                            isSelected={!!selectedOrganization}
                            validationErrors={validationErrors}
                          />
                        </Box>
                      </Grid>
                      {shouldShowVersionFlow && versionableAgreements.length > 1 && (
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Select Agreement</InputLabel>
                            <Select
                              value={selectedAgreementIndex}
                              label="Select Agreement"
                              onChange={e => {
                                setSelectedAgreementIndex(Number(e.target.value))
                                setChangeType('')
                                setSelectedVersionOverride(null)
                              }}
                            >
                              {versionableAgreements.map((a, i) => (
                                <MenuItem key={a.masterAgreementId} value={i}>
                                  {a.agreementFamily} — v{a.currentVersion?.semanticVersion || '?'} ({a.versions.length} version
                                  {a.versions.length !== 1 ? 's' : ''})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Box ref={agreementOptionRef}>
                          <SelectAgreementOption
                            agreementOption={agreementOption}
                            onChange={handleAgreementOption}
                            validationErrors={validationErrors}
                            isSelected={!!selectedOrganization}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SelectInvoiceOption
                          invoiceOption={invoiceOption}
                          onChange={handleInvoiceOptionChange}
                          validationErrors={validationErrors}
                          isSelected={!!selectedOrganization}
                          isCloning={agreementOption && agreementOption === 'Replace Existing Business' ? true : false}
                        />
                      </Grid>
                      {agreementOption && agreementOption === 'Replace Existing Business' && (
                        <Grid item xs={12} sm={12}>
                          <DealsAutocomplete
                            deals={organizationDeals ?? []}
                            label={'Select deal to be cloned'}
                            selectedDeal={selectedDeal}
                            setSelectedDeal={setSelectedDeal}
                            onAddDeal={addPredecessorSelection}
                            onClearDeal={handleClearDeal}
                            error={validationErrors?.selectedDeal}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Grid>
                <Grid container alignItems="center">
                  <Grid item xs={8}>
                    <Box ref={stageRef}>
                      <StageDefaultTextInput stage={localStage} handleChange={handleChange} />
                    </Box>
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box ref={noSignatureRef}>
                      <ExistingDeal
                        noSignature={!!newDeal?.noSignature}
                        newDeal={newDeal}
                        organization={selectedOrganization}
                        handleChange={onNoSignatureChange}
                        context={'createDeal'}
                      />
                    </Box>
                  </Grid>
                </Grid>
                {hasVersionableAgreements && changeType ? (
                  <Grid container alignItems="center">
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Deal Type:
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 600, color: newDeal?.type ? renderColorByString(newDeal?.type) : '' }}
                        >
                          {newDeal.type}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          {isVersionChangeType ? '(inherited from current version)' : '(auto-selected)'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container alignItems="center">
                    <Grid item xs={12} xl={12}>
                      <Box ref={typeRef}>
                        <DealTypeSelect
                          newDeal={newDeal}
                          setNewDeal={setNewDeal}
                          error={selectedOrganization?.length < 1 ? false : validationErrors.type}
                          helperText={validationErrors.type && 'Deal type is required'}
                          onChange={handleDealTypeChange}
                          validationErrors={validationErrors}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
                <ContactsAutocomplete
                  contacts={allContacts}
                  selectedContacts={selectedContacts}
                  setSelectedContacts={setSelectedContacts}
                  label="Select Contacts"
                  onChange={handleAddContact}
                  validationErrors={validationErrors}
                />
                {shouldShowVersionFlow && changeType ? (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {changeType === 'minor' ? (
                      /* Minor (date renewal): both dates editable */
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DatePicker
                          label="Start Date"
                          value={newDeal.startDate ? dayjs(newDeal.startDate) : null}
                          onChange={newDate => {
                            setNewDeal(prev => ({
                              ...prev,
                              startDate: normalizeDate(newDate, 'America/New_York'),
                            }))
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: validationErrors.startDate,
                              helperText: validationErrors.startDate
                                ? 'Start date required'
                                : 'Renewal start date',
                            },
                          }}
                        />
                        <DatePicker
                          label="End Date"
                          value={newDeal.endDate ? dayjs(newDeal.endDate) : null}
                          minDate={newDeal.startDate ? dayjs(newDeal.startDate) : undefined}
                          onChange={newDate => {
                            setNewDeal(prev => ({
                              ...prev,
                              endDate: normalizeDate(newDate, 'America/New_York'),
                            }))
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: validationErrors.endDate,
                              helperText: validationErrors.endDate
                                ? newDeal.endDate
                                  ? 'End date must be after start date'
                                  : 'End date required'
                                : 'Renewal end date',
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      /* Major/Patch: auto-calculated start, editable end */
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          label="Start Date (auto-calculated)"
                          value={newDeal.startDate ? dayjs(newDeal.startDate).format('MM/DD/YYYY') : ''}
                          disabled
                          fullWidth
                          helperText="Aligns with previous version end date"
                          sx={{
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'background.defaultdim',
                            },
                          }}
                        />
                        <DatePicker
                          label="End Date"
                          value={newDeal.endDate ? dayjs(newDeal.endDate) : null}
                          minDate={newDeal.startDate ? dayjs(newDeal.startDate) : undefined}
                          onChange={newDate => {
                            setNewDeal(prev => ({
                              ...prev,
                              endDate: normalizeDate(newDate, 'America/New_York'),
                            }))
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: validationErrors.endDate,
                              helperText: validationErrors?.endDate
                                ? newDeal.endDate
                                  ? 'End date must be after start date'
                                  : 'End date required'
                                : 'Agreement end date',
                            },
                          }}
                        />
                      </Box>
                    )}
                  </LocalizationProvider>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DateRangePicker
                        localeText={{ start: 'Expected Start Date', end: 'Expected Close Date' }}
                        value={[
                          newDeal.startDate ? dayjs(newDeal.startDate) : null,
                          newDeal.endDate ? dayjs(newDeal.endDate) : null,
                        ]}
                        onChange={newValue => {
                          const [startDate, endDate] = newValue

                          setNewDeal(prev => ({
                            ...prev,
                            startDate: normalizeDate(startDate, 'America/New_York'),
                            endDate: normalizeDate(endDate, 'America/New_York'),
                          }))
                        }}
                        slotProps={{
                          textField: {
                            error: validationErrors?.organization
                              ? false
                              : validationErrors.startDate || validationErrors.endDate,
                          },
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiMultiInputDateRangeField-separator': {
                            m: 0,
                          },
                          '& .MuiFormControl-root': {
                            m: 0,
                          },
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                            bgcolor: validationErrors?.organization ? 'background.defaultdim' : '',
                            m: 0,
                          },
                        }}
                        disabled={validationErrors?.organization}
                      />
                    </Box>
                  </LocalizationProvider>
                )}
                {activeUser && activeUser.user && testUsers.includes(activeUser.user.id) ? (
                  <Grid container alignItems="center">
                    <Grid item xs={12}>
                      <CreateProjectTextField
                        label="Probability (%)"
                        name="probability"
                        value={newDeal.probability}
                        onChange={handleChange}
                        fullWidth
                        autoComplete="off"
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 1, // Controls the increment/decrement step
                        }}
                        disabled={validationErrors?.organization}
                        sx={{
                          '& .MuiInputBase-root': {
                            bgcolor: validationErrors?.organization ? 'background.defaultdim' : '',
                          },
                        }}
                      />
                    </Grid>
                    {/* <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <TestDealCheck isTest={isTest} setIsTest={setIsTest} setNewDeal={setNewDeal} />
                  </Grid> */}
                  </Grid>
                ) : (
                  <CreateProjectTextField
                    label="Probability (%)"
                    name="probability"
                    value={newDeal.probability}
                    onChange={handleChange}
                    fullWidth
                    autoComplete="off"
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 1, // Controls the increment/decrement step
                    }}
                  />
                )}
              </Stack>
            )}
          </DialogContent>
        </BoxScroller>
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              sx={{
                marginLeft: 4,
                backgroundColor: 'rgb(244, 67, 54, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgb(244, 67, 54, 0.7)',
                },
              }}
              onClick={() => handleCloseCreateDeal(null, null)}
              color="primary"
              variant="outlined"
            >
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }} ref={nextRef}>
              <Button
                onClick={event => {
                  // For major/minor/patch with versioning, open the amendment dialog instead
                  if (shouldShowVersionFlow && (changeType === 'major' || changeType === 'minor' || changeType === 'patch')) {
                    if (!validateFields()) return
                    setShowAmendmentDialog(true)
                    return
                  }

                  // Existing behavior for major, io, csa, and non-versioning flows
                  const dealToSave = shouldShowVersionFlow
                    ? {
                        ...(newDeal as any),
                        semanticVersion: nextVersion,
                        masterAgreementId: activeAgreement?.masterAgreementId,
                        versionChangeType: changeType,
                        versionType: changeType,
                      }
                    : newDeal
                  handleSaveDealAndNavigate({
                    event,
                    newDeal: (dealToSave as unknown) as OrganizationTableItem,
                    assignedProducts: relatedProducts,
                    setNewDeal,
                    setIsCreating,
                    resetDealState,
                    onClose,
                    setIsError,
                    validateFields,
                    cloneMode: shouldShowVersionFlow ? 'version' : undefined,
                  })
                }}
                disabled={isCreating || !isValid}
                color="secondary"
                variant="contained"
              >
                Next
              </Button>
            </Box>
          </Box>
        </DialogActions>
        {selectedDeal && (
          <CreateDealDisclaimer
            newDeal={(newDeal as unknown) as Deal}
            selectedDeal={selectedDeal}
            relatedProducts={relatedProducts}
          />
        )}
      </Dialog>
      <HelpOverlay />
      <FormHelpPopover
        steps={createNewDealSteps}
        anchorEl={currentStep !== null ? stepRefs[currentStep]?.current : null}
        stepIndex={currentStep !== null ? (currentStep as any)?.stepIndex : null}
      />
      <HelpNavigationBox totalSteps={createNewDealSteps.length} />
      <FloatingHelpButton startHelp={handleStartHelp} />
      {hasMultipleActiveVersions && activeAgreement && (
        <AgreementComparisonDialog
          open={showComparisonDialog}
          onClose={() => setShowComparisonDialog(false)}
          activeVersions={activeVersionsForAgreement}
          selectedVersion={selectedVersionOverride}
          onSelect={setSelectedVersionOverride}
          agreementFamily={activeAgreement.agreementFamily}
        />
      )}
      {showAmendmentDialog && activeCurrentVersion && activeAgreement && (
        <VersionAmendmentDialog
          open={showAmendmentDialog}
          onClose={() => setShowAmendmentDialog(false)}
          currentVersion={activeCurrentVersion}
          activeAgreement={activeAgreement}
          changeType={changeType as 'major' | 'minor' | 'patch'}
          newDealBase={newDeal}
          nextVersion={nextVersion}
          onSaveComplete={() => {
            setShowAmendmentDialog(false)
            resetDealState()
            onClose()
          }}
        />
      )}
    </Box>
  )
}

export default CreateVersionContract
